/**
 * @fileoverview Google OAuth signup button component
 * @description Button to initiate Google OAuth signup flow and create account
 */

import { useState, useEffect, memo } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { IconX } from '@tabler/icons-react'
import { toast } from 'react-toastify'
import { EmailAlreadyRegisteredError } from 'helpers'
import firebase from 'firebase/app'
import GoogleIcon from '@legacy/common/Icons/Google'
import { initFirebaseApp } from '@legacy/common/Tools/Firebase'
import { useSignupContext } from '../context/SignupContext'
import { submitGoogleSignup } from '../service'
import type { FirebaseUser, FirebaseTokenManager } from '../service/types'

interface GoogleSignupButtonProps {
  /** Whether button is disabled */
  readonly disabled?: boolean
}

/**
 * Google OAuth signup button
 * Handles Firebase popup authentication and backend account creation
 */
export const GoogleSignupButton = memo(({ disabled }: GoogleSignupButtonProps) => {
  const { t } = useTranslation()
  const { setUser, setStep, setGoogleAuth } = useSignupContext()
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    initFirebaseApp()
  }, [])

  /**
   * Handle Google signup flow:
   * 1. Open Firebase Google popup
   * 2. Get user credentials from Google
   * 3. Call backend signup endpoint to create account
   * 4. Store user in context and advance to Step 2
   */
  const handleGoogleSignup = async () => {
    setLoading(true)

    try {
      // Step 1: Firebase Google OAuth popup
      const provider = new firebase.auth.GoogleAuthProvider()
      const result = await firebase.auth().signInWithPopup(provider)

      if (!result.user) {
        throw new Error(t('teacher_signup.google_no_user_data'))
      }

      const { user } = result

      // Step 2: Extract user data
      const firebaseUser: FirebaseUser = {
        email: user.email || '',
        uid: user.uid,
        displayName: user.displayName || undefined,
        firstName: user.displayName?.split(' ')[0],
        lastName: user.displayName?.split(' ').slice(1).join(' ') || undefined,
      }

      // Get token manager from user object (for backend validation)
      const tokenManager = (user as any).stsTokenManager as FirebaseTokenManager | undefined

      // Step 3: Call backend to create account
      const response = await submitGoogleSignup(firebaseUser, tokenManager)

      // Step 4: Store user in context and advance to Step 2
      setUser(response.user)
      if (user.refreshToken) {
        setGoogleAuth(user.refreshToken)
      }

      // Show success message
      toast.success(t('teacher_signup.google_signup_success'))

      setStep(2)

    } catch (error: any) {
      // Handle Firebase popup errors (silent - user action)
      if (error.code === 'auth/popup-closed-by-user') {
        return // User closed popup intentionally
      }

      if (error.code === 'auth/cancelled-popup-request') {
        return // Another popup was already open
      }

      // Handle Firebase popup blocked
      if (error.code === 'auth/popup-blocked') {
        toast.error(t('teacher_signup.popup_blocked'))
        return
      }

      // Handle network errors
      if (error.code === 'auth/network-request-failed') {
        toast.error(t('teacher_signup.network_error'))
        return
      }

      // Handle email already registered
      if (error instanceof EmailAlreadyRegisteredError) {
        showNotification({
          title: 'Email Already Registered',
          message: 'The email address you entered is already associated with an existing account. Please log in or use a different email to sign up.',
          color: 'red',
          icon: <IconX size={18} />,
          autoClose: 8000,
        })
        return
      }

      // Handle backend/other errors
      const errorMessage = error instanceof Error
        ? error.message
        : t('teacher_signup.google_signup_error')
      toast.error(errorMessage)

    } finally {
      setLoading(false)
    }
  }

  return (
    <Button
      fullWidth
      size='lg'
      variant='default'
      leftIcon={<GoogleIcon />}
      disabled={disabled || loading}
      loading={loading}
      onClick={handleGoogleSignup}
      aria-label={t('teacher_signup.sign_up_with_google')}
    >
      {t('teacher_signup.sign_up_with_google')}
    </Button>
  )
})

GoogleSignupButton.displayName = 'GoogleSignupButton'
